import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from '../hooks/useAuth';
import { getSettings, updateSettings, syncNow, syncStop, getSyncStatus } from '../api/sharepointAPI';
import { useNotification } from '../context/NotificationContext';
import { NotificationType } from '../enums/NotificationType';
import ConnectionsList from '../components/sharepoint/ConnectionsList';
import { formatDateTime } from '../utils/dateFormat';
import '../css/pages/sharepoint.css';

const PRESETS = [21600, 43200, 86400]; // 6 h / 12 h / 24 h

const SharePoint = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [t, i18n] = useTranslation('global');
  const { showNotification } = useNotification();

  const [enabled, setEnabled] = useState(false);
  const [intervalSec, setIntervalSec] = useState(43200);
  const [preset, setPreset] = useState('43200');   // '21600' | '43200' | '86400' | 'custom'
  const [customHours, setCustomHours] = useState(12);
  const [lastSync, setLastSync] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (Number(auth?.uri) !== 1) { navigate('/Unauthorized'); return; }
    getSettings()
      .then(s => {
        const sec = s.SyncIntervalSeconds || 43200;
        setEnabled(!!s.Enabled);
        setIntervalSec(sec);
        setPreset(PRESETS.includes(sec) ? String(sec) : 'custom');
        setCustomHours(Math.max(1, Math.round(sec / 3600)));
        setLastSync(s.LastSyncAt || null);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Živý stav behu — polling každé 4 s (a obnova „naposledy" po dobehnutí).
  useEffect(() => {
    if (Number(auth?.uri) !== 1) return undefined;
    const tick = () => {
      getSyncStatus()
        .then(s => {
          setRunning(!!s.Running);
          if (s.LastSyncAt) setLastSync(s.LastSyncAt);
        })
        .catch(() => {});
    };
    tick();
    const timer = window.setInterval(tick, 4000);
    return () => window.clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hours = Math.max(1, Math.round(intervalSec / 3600));
  const lastRunText = lastSync ? formatDateTime(lastSync, i18n.language) : (t('never') || 'nikdy');

  const handleToggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try {
      await updateSettings({ Enabled: next });
    } catch {
      setEnabled(!next);
      showNotification({ message: t('errorOccured'), type: NotificationType.Error });
    }
  };

  const saveInterval = async (sec) => {
    setIntervalSec(sec);
    try {
      await updateSettings({ SyncIntervalSeconds: sec });
      showNotification({ message: t('savedChanges'), type: NotificationType.Success });
    } catch {
      showNotification({ message: t('errorOccured'), type: NotificationType.Error });
    }
  };

  const handlePresetChange = (e) => {
    const val = e.target.value;
    setPreset(val);
    if (val !== 'custom') saveInterval(Number(val));
  };

  const handleSyncNow = async () => {
    setRunning(true); // optimisticky; polling potvrdí/zhasne
    try {
      await syncNow();
      showNotification({ message: t('syncStarted') || 'Synchronizácia spustená', type: NotificationType.Success });
    } catch {
      setRunning(false);
      showNotification({ message: t('errorOccured'), type: NotificationType.Error });
    }
  };

  const handleStop = async () => {
    try {
      await syncStop();  // zastaví aktuálny beh; polling zhasne „running", keď reálne skončí
      showNotification({ message: t('stopRequested') || 'Zastavujem…', type: NotificationType.Info });
    } catch {
      showNotification({ message: t('errorOccured'), type: NotificationType.Error });
    }
  };

  return (
    <div className="documents-wrap sp-page">
      <button className="return-button return-button-top" onClick={() => navigate('/')}>
        <span className="material-symbols-outlined">arrow_back</span>{t('return')}
      </button>
      <div className="header-title">
        <h1>{t('sharePoint') || 'SharePoint synchronizácia'}</h1>
      </div>
      <p className="sp-page-sub">{t('sharePointSubtitle') || 'Automaticky sťahuje dokumenty zo SharePointu do prostredí aplikácie.'}</p>

      {/* ── Stavová karta ── */}
      <div className="sp-card sp-hero sp-card-flat">
        <div className="sp-hero-main">
          <span className={`sp-hero-ico material-symbols-outlined${running ? ' running' : enabled ? ' on' : ' off'}`}>
            {running ? 'sync' : enabled ? 'cloud_done' : 'cloud_off'}
          </span>
          <div className="sp-hero-text">
            <div className="sp-hero-title">
              {enabled ? (t('syncOn') || 'Synchronizácia je zapnutá') : (t('syncOff') || 'Synchronizácia je vypnutá')}
            </div>
            <div className="sp-hero-sub">
              {(t('runsEvery') || 'Spúšťa sa každých')} {hours} h · {(t('lastRun') || 'naposledy')}: {lastRunText}
            </div>
          </div>
          <div className="sp-hero-actions">
            <button className={`sp-sync-now${running ? ' running' : ''}`} onClick={handleSyncNow} disabled={!enabled || running}>
              <span className={`material-symbols-outlined${running ? ' spin' : ''}`}>{running ? 'progress_activity' : 'sync'}</span>
              &nbsp;{running ? (t('syncRunning') || 'Prebieha synchronizácia…') : (t('syncNow') || 'Synchronizovať teraz')}
            </button>
            {running && (
              <button className="sp-sync-stop" onClick={handleStop}>
                <span className="material-symbols-outlined">stop</span>&nbsp;{t('stopSync') || 'Zastaviť'}
              </button>
            )}
            <label className="switch sp-hero-switch" title={enabled ? t('syncOn') : t('syncOff')}>
              <input type="checkbox" checked={enabled} onChange={handleToggle} />
              <span className="slider"></span>
            </label>
          </div>
        </div>
        <div className="sp-hero-interval">
          <label>{t('runEveryLabel') || 'Spúšťať každých'}</label>
          <select value={preset} onChange={handlePresetChange} className="sp-select">
            <option value="21600">{t('every6h') || '6 hodín'}</option>
            <option value="43200">{t('every12h') || '12 hodín'}</option>
            <option value="86400">{t('every24h') || '24 hodín'}</option>
            <option value="custom">{t('custom') || 'Vlastné…'}</option>
          </select>
          {preset === 'custom' && (
            <>
              <input type="number" min="1" value={customHours} onChange={e => setCustomHours(e.target.value)} style={{ width: '5em' }} />
              <span>{t('hoursUnit') || 'h'}</span>
              <button className="download-button" onClick={() => saveInterval(Math.max(1, Number(customHours)) * 3600)}>{t('save')}</button>
            </>
          )}
          <span className="sp-hint">{t('intervalHint') || 'Ako často sa dokumenty automaticky skontrolujú a stiahnu.'}</span>
        </div>
      </div>

      <ConnectionsList />
    </div>
  );
};

export default SharePoint;
